import type { ComputedRef } from 'vue'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import type { AppBadgeColor } from '../../app-badge'
import type { BadgeBookingStatusProps } from '../types/interfaces'

import { Status as BookingStatus, ReasonCategory } from '@brighterly/lib-core-types/booking/enums'

import {
  faCheck,
  faCheckDouble,
  faHourglass,
  faClockRotateLeft,
  faFile,
  faCalendar,
  faGear,
  faUser,
  faGraduationCap,
} from '@fortawesome/pro-regular-svg-icons'

import { useListsStore } from '@shared/model'

import { useBadgeFromStatus } from '../../app-badge'

interface BadgeBookingStatusOptions {
  props: BadgeBookingStatusProps
}

interface BadgeBookingStatusReturn {
  color: ComputedRef<AppBadgeColor>
  text: ComputedRef<string>
  icon: ComputedRef<IconDefinition>
}

type IconsMap = Partial<Record<ReasonCategory, IconDefinition>> & {
  default: IconDefinition
}

const STATUS_COLORS: Partial<Record<BookingStatus, AppBadgeColor>> = {
  [BookingStatus.Confirmed]: 'blue',
  [BookingStatus.InProgress]: 'blue',
  [BookingStatus.ReservedPlanned]: 'slate',
  [BookingStatus.ReservedConfirmed]: 'slate',
  [BookingStatus.Draft]: 'slate',
  [BookingStatus.Planned]: 'slate',
  [BookingStatus.Completed]: 'emerald',
  [BookingStatus.NoShow]: 'red',
  [BookingStatus.Canceled]: 'orange',
  [BookingStatus.CanceledBad]: 'orange',
  [BookingStatus.Rescheduled]: 'amber',
  [BookingStatus.RescheduledBad]: 'amber',
  [BookingStatus.PendingRescheduled]: 'amber',
}

const REASON_ICONS: IconsMap = {
  default: faGear,
  [ReasonCategory.Tutor]: faGraduationCap,
  [ReasonCategory.Customer]: faUser,
}

const STATUS_ICONS: Partial<Record<BookingStatus, IconsMap>> = {
  [BookingStatus.Confirmed]: { default: faCheck },
  [BookingStatus.InProgress]: { default: faHourglass },
  [BookingStatus.ReservedPlanned]: { default: faClockRotateLeft },
  [BookingStatus.ReservedConfirmed]: { default: faClockRotateLeft },
  [BookingStatus.Draft]: { default: faFile },
  [BookingStatus.Planned]: { default: faCalendar },
  [BookingStatus.Completed]: { default: faCheckDouble },
  [BookingStatus.NoShow]: REASON_ICONS,
  [BookingStatus.Canceled]: REASON_ICONS,
  [BookingStatus.CanceledBad]: REASON_ICONS,
  [BookingStatus.Rescheduled]: REASON_ICONS,
  [BookingStatus.RescheduledBad]: REASON_ICONS,
  [BookingStatus.PendingRescheduled]: REASON_ICONS,
}

export const useBadgeBookingStatus = ({ props }: BadgeBookingStatusOptions): BadgeBookingStatusReturn => {
  const listsStore = useListsStore()

  const { color, text } = useBadgeFromStatus<BookingStatus>({
    status: () => props.status,
    colors: STATUS_COLORS,
    labels: () => listsStore.getLists?.booking.status,
  })

  const icon = computed<IconDefinition>(() => {
    const entry = STATUS_ICONS[props.status]
    if (!entry) return faGear
    const category = props.category
    return (category && entry[category]) ?? entry.default
  })

  return {
    color,
    text,
    icon,
  }
}
